import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Clipboard, Image, KeyboardAvoidingView, Linking, Modal, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import Icon from "react-native-vector-icons/Ionicons";
import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Button, Input } from "../../shared/ui/FormElements";
import { UserService } from "../../services/UserService";
import { UserDeviceService } from "../../services/UserDeviceService";
import { UserRegistrationService } from "../../services/UserRegistrationService";
import { useAuth } from "../../../context/AuthContext";
import { AuthStackParamList } from "../../navigation/navigationTypes";
import { Flash } from "../../shared/utils/Flash";
import { UserAuthModel } from "../../models/UserAuthModel";
import { RegistrationOtpModel } from "../../models/RegistrationOtpModel";
import { OtpChannelInfoModel, VerificationParameterModel } from "../../models/OtpVerificationModel";
import { getMaskedOtpLabel, hasAnyVerificationChannel, hasOtpValue, normalizeOtpChannelInfo, normalizeVerificationParameter, OTP_MAX_LENGTH, OTP_PLACEHOLDER } from "../../shared/utils/verification";
import ApprovalConfirmModal from "../../shared/utils/ApprovalConfirmModal";

type Nav = NativeStackNavigationProp<AuthStackParamList>;
type LoginMethod = "mobile" | "email";
type ReverifyRequest = { mobileNumber: string | null; email: string | null };

const EMPTY_CONFIG: VerificationParameterModel = { email: false, mobile: false };
const FALLBACK_LOGIN_CONFIG: VerificationParameterModel = { email: false, mobile: true };
const EMPTY_OTP_CHANNELS: OtpChannelInfoModel = { email: false, mobile: false, maskedEmail: null, maskedMobileNumber: null };
const RESEND_OTP_SECONDS = 5 * 60;
const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export function LoginScreen() {
    const navigation = useNavigation<Nav>();
    const { setUser } = useAuth();
    const [identifier, setIdentifier] = useState("");
    const [loginMethod, setLoginMethod] = useState<LoginMethod>("mobile");
    const [verificationConfig, setVerificationConfig] = useState<VerificationParameterModel>(EMPTY_CONFIG);
    const [mpin, setMpin] = useState("");
    const [loading, setLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [showMpin, setShowMpin] = useState(false);
    const [showReverifyModal, setShowReverifyModal] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [reverifyMobileNumber, setReverifyMobileNumber] = useState("");
    const [reverifyEmail, setReverifyEmail] = useState("");
    const [reverifyError, setReverifyError] = useState("");
    const [reverifyOtpToken, setReverifyOtpToken] = useState("");
    const [otpDeliveryInfo, setOtpDeliveryInfo] = useState<OtpChannelInfoModel>(EMPTY_OTP_CHANNELS);
    const [mobileOtp, setMobileOtp] = useState("");
    const [emailOtp, setEmailOtp] = useState("");
    const [otpErrorMessage, setOtpErrorMessage] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [reverifyRequest, setReverifyRequest] = useState<ReverifyRequest | null>(null);

    const mobileEnabled = verificationConfig.mobile;
    const emailEnabled = verificationConfig.email;
    const canChooseLoginMethod = mobileEnabled && emailEnabled;
    const trimmedIdentifier = useMemo(() => identifier.trim(), [identifier]);
    const normalizedIdentifier = useMemo(() => (loginMethod === "email" ? trimmedIdentifier.toLowerCase() : trimmedIdentifier), [loginMethod, trimmedIdentifier]);
    const reverifyMobileTrimmed = useMemo(() => reverifyMobileNumber.trim(), [reverifyMobileNumber]);
    const reverifyEmailTrimmed = useMemo(() => reverifyEmail.trim(), [reverifyEmail]);
    const resendTimeLabel = useMemo(() => `${String(Math.floor(resendTimer / 60)).padStart(2, "0")}:${String(resendTimer % 60).padStart(2, "0")}`, [resendTimer]);
    const mobileOtpValid = useMemo(() => hasOtpValue(mobileOtp), [mobileOtp]);
    const emailOtpValid = useMemo(() => hasOtpValue(emailOtp), [emailOtp]);
    const canVerifyOtp = useMemo(() => {
        if (otpDeliveryInfo.mobile && otpDeliveryInfo.email) return mobileOtpValid || emailOtpValid;
        if (otpDeliveryInfo.mobile) return mobileOtpValid;
        if (otpDeliveryInfo.email) return emailOtpValid;
        return false;
    }, [emailOtpValid, mobileOtpValid, otpDeliveryInfo]);
    const identifierLabel = loginMethod === "email" ? "Email Address" : "Mobile Number";
    const identifierPlaceholder = useMemo(() => {
        if (canChooseLoginMethod) return loginMethod === "email" ? "Enter your registered email address" : "Enter your 10 digit mobile number";
        return emailEnabled ? "Enter your registered email address" : "Enter your 10 digit mobile number";
    }, [canChooseLoginMethod, emailEnabled, loginMethod]);
    const otpVerificationHint = useMemo(() => (otpDeliveryInfo.mobile && otpDeliveryInfo.email ? "Use any one valid 4-digit OTP to continue." : "Use the 4-digit OTP sent to your registered contact."), [otpDeliveryInfo]);
    const mobileOtpLabel = useMemo(() => getMaskedOtpLabel("Mobile OTP", otpDeliveryInfo.maskedMobileNumber), [otpDeliveryInfo.maskedMobileNumber]);
    const emailOtpLabel = useMemo(() => getMaskedOtpLabel("Email OTP", otpDeliveryInfo.maskedEmail), [otpDeliveryInfo.maskedEmail]);
    const isReverifyMobileDisabled = useMemo(
        () => !!reverifyEmailTrimmed,
        [reverifyEmailTrimmed]
    );
    const isReverifyEmailDisabled = useMemo(
        () => !!reverifyMobileTrimmed,
        [reverifyMobileTrimmed]
    );
    const [authErrorDialogVisible, setAuthErrorDialogVisible] = useState(false);
    const [authErrorMessage, setAuthErrorMessage] = useState("");
    const closeAuthErrorDialog = () => {
        setAuthErrorDialogVisible(false);
        setAuthErrorMessage("");
    };

    const handleSupportEmailPress = () => {
        const email = "info@gaurpremaashray.co.in";

        Clipboard.setString(email);
        Flash.success("Copied", "Email address copied to clipboard");
    };

    useEffect(() => {
        let mounted = true;
        const loadLoginConfig = async () => {
            setConfigLoading(true);
            try {
                
                const res = await UserService.getLoginConfig();
                
                if (res?.status !== 200) {
                    if (mounted) {
                        setVerificationConfig(FALLBACK_LOGIN_CONFIG);
                        setLoginMethod("mobile");
                    }
                    return;
                }
                if (!mounted) return;
                const normalizedConfig = normalizeVerificationParameter(res?.data);
                const resolvedConfig = normalizedConfig.mobile || normalizedConfig.email ? normalizedConfig : FALLBACK_LOGIN_CONFIG;
                setVerificationConfig(resolvedConfig);
                setLoginMethod(resolvedConfig.mobile ? "mobile" : "email");
            } catch {
                if (mounted) {
                    setVerificationConfig(FALLBACK_LOGIN_CONFIG);
                    setLoginMethod("mobile");
                }
            } finally {
                if (mounted) setConfigLoading(false);
            }
        };
        loadLoginConfig();
        return () => { mounted = false; };
    }, []);

    useEffect(() => { setIdentifier(""); }, [loginMethod]);
    useEffect(() => {
        if (!configLoading && !(mobileEnabled && emailEnabled)) {
            const nextMethod: LoginMethod = mobileEnabled ? "mobile" : "email";
            if (loginMethod !== nextMethod) setLoginMethod(nextMethod);
        }
    }, [configLoading, emailEnabled, loginMethod, mobileEnabled]);
    useEffect(() => {
        if (resendTimer <= 0) return;
        const timer = setInterval(() => setResendTimer((current) => (current > 0 ? current - 1 : 0)), 1000);
        return () => clearInterval(timer);
    }, [resendTimer]);

    const getResolvedUser = (res: any) => (Array.isArray(res?.data) ? res.data[0] : res?.data);
    const showOtpError = (message: string) => { setOtpErrorMessage(message); };
    const resetOtpModalState = () => {
        setShowOtpModal(false);
        setReverifyOtpToken("");
        setOtpDeliveryInfo(EMPTY_OTP_CHANNELS);
        setMobileOtp("");
        setEmailOtp("");
        setOtpErrorMessage("");
        setResendTimer(0);
        setReverifyRequest(null);
    };
    const closeReverifyFlow = () => {
        setShowReverifyModal(false);
        resetOtpModalState();
        setReverifyError("");
        setReverifyMobileNumber("");
        setReverifyEmail("");
    };
    const prefillReverifyContacts = () => {
        const nextEmail = loginMethod === "email" ? normalizedIdentifier : "";
        const nextMobile = nextEmail ? "" : loginMethod === "mobile" ? normalizedIdentifier : "";

        setReverifyMobileNumber(nextMobile);
        setReverifyEmail(nextEmail);
        setReverifyError("");
    };
    const handleAuthenticatedUser = async (
        res: any,
        fallbackMobileNumber?: string | null,
        sessionUuid?: string | null
    ) => {
        const resolvedUser = getResolvedUser(res);
        const uuid = sessionUuid?.trim() || await UserDeviceService.getCurrentUuid();
        await setUser({
            token: res?.token || "",
            mobileNumber: resolvedUser?.mobileNumber ?? fallbackMobileNumber ?? "",
            uuid,
            name: resolvedUser?.name,
            userId: resolvedUser?.userId,
            raw: resolvedUser ?? res,
            userType: resolvedUser?.userType,
        });
        try {
            await UserDeviceService.registerCurrentDevice(resolvedUser?.userId);
        } catch (error) {
            console.warn("Failed to register login device", error);
        }
    };
    const startOtpFlow = (res: any, request: ReverifyRequest) => {
        const otpInfo = normalizeOtpChannelInfo(res?.data);
        if (res?.status !== 200) return Flash.error(res?.message ?? "Re-verification request failed.");
        if (!res?.token) return Flash.error("Verification session could not be created. Please try again.");
        if (!hasAnyVerificationChannel(otpInfo)) return Flash.error("No verification channel is enabled for this account.");
        setReverifyRequest(request);
        setReverifyOtpToken(res.token);
        setOtpDeliveryInfo(otpInfo);
        setMobileOtp("");
        setEmailOtp("");
        setOtpErrorMessage("");
        setResendTimer(RESEND_OTP_SECONDS);
        setShowReverifyModal(false);
        setShowOtpModal(true);
        // Flash.success("Success!", "OTP sent successfully");
    };
    const submitReverifyRequest = async () => {
        const nextMobileNumber = reverifyMobileTrimmed || null;
        const nextEmail = reverifyEmailTrimmed ? reverifyEmailTrimmed.toLowerCase() : null;
        if (!nextMobileNumber && !nextEmail) return setReverifyError("Enter at least one mobile number or email address.");
        if (nextMobileNumber && nextMobileNumber.length !== 10) return setReverifyError("Please enter a valid 10 digit mobile number.");
        if (nextEmail && !isValidEmail(nextEmail)) return setReverifyError("Please enter a valid email address.");
        setLoading(true);
        setReverifyError("");
        try {
            const res = await UserRegistrationService.reverifyAccount(nextMobileNumber, nextEmail);
            startOtpFlow(res, { mobileNumber: nextMobileNumber, email: nextEmail });
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "Unable to start account re-verification.";
            setReverifyError(msg);
            // Flash.error(msg);
        } finally {
            setLoading(false);
        }
    };
    const handleResendOtp = async () => {
        if (!reverifyRequest) return showOtpError("Verification details are missing. Please try again.");
        setLoading(true);
        setOtpErrorMessage("");
        try {
            const res = await UserRegistrationService.reverifyAccount(reverifyRequest.mobileNumber, reverifyRequest.email);
            startOtpFlow(res, reverifyRequest);
        } catch (err: any) {
            showOtpError(err?.response?.data?.message || err?.message || "Failed to resend OTP.");
        } finally {
            setLoading(false);
        }
    };
    const handleVerifyOtp = async () => {
        if (!reverifyOtpToken) return showOtpError("Verification session expired. Please request OTP again.");
        const payload: RegistrationOtpModel = {};
        if (otpDeliveryInfo.mobile && mobileOtpValid) payload.mobileOtp = mobileOtp.trim();
        if (otpDeliveryInfo.email && emailOtpValid) payload.emailOtp = emailOtp.trim();
        if (!payload.mobileOtp && !payload.emailOtp) {
            if (otpDeliveryInfo.mobile && otpDeliveryInfo.email) return showOtpError("Enter at least one valid OTP to continue.");
            return showOtpError(otpDeliveryInfo.mobile ? "Enter the mobile OTP." : "Enter the email OTP.");
        }
        setLoading(true);
        setOtpErrorMessage("");
        try {
            const res = await UserRegistrationService.verifyOtp(payload, reverifyOtpToken);
            if (res?.status === 401) {
                
                Flash.error("Session Expired", res?.message ?? "Unauthorized. Please login again.");

                resetOtpModalState();
                setReverifyMobileNumber("");
                setReverifyEmail("");

                navigation.navigate("Login"); // or navigation.replace("Login")
                return;
            }

            if (res?.status !== 200) {
                return showOtpError(res?.message ?? "OTP verification failed.");
            }

            if (!res?.token) {
                return showOtpError("Login token was not returned after OTP verification.");
            }

            await handleAuthenticatedUser(res, reverifyRequest?.mobileNumber ?? null);
            resetOtpModalState();
            setReverifyMobileNumber("");
            setReverifyEmail("");
            Flash.success("Hare Krishna!", "Your account has been verified successfully.");
        } catch (err: any) {
            showOtpError(err?.response?.data?.message || err?.message || "OTP verification failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async () => {
        if (configLoading) return Flash.error("Loading sign-in options. Please wait a moment.");
        if (!mobileEnabled && !emailEnabled) return Flash.error("Hare Krishna. Sign in is not available right now. Please contact support.");
        if (loginMethod === "email") {
            if (!isValidEmail(trimmedIdentifier)) return Flash.error("Please enter a valid email address");
        } else if (trimmedIdentifier.length !== 10) {
            return Flash.error("Please enter 10 digit mobile number");
        }
        if (mpin.length !== 6) return Flash.error("MPIN must be 6 digits");
        setLoading(true);
        try {
            const uuid = await UserDeviceService.getCurrentUuid();
            const payload: UserAuthModel = {
                mobileNumber: normalizedIdentifier,
                password: mpin,
                uuid,
            };
            const res = await UserService.userAuth(payload);
            if (res?.status === 0) {
                prefillReverifyContacts();
                setReverifyError(res?.message ?? "Your account needs re-verification.");
                setShowReverifyModal(true);
                return;
            }

            if (res?.status === 401) {
                setAuthErrorMessage(res?.message ?? "Unauthorized access.");
                setAuthErrorDialogVisible(true);
                return;
            }

            if (res?.status !== 200) return Flash.error(res?.message ?? "Login failed.");
            const resolvedUser = getResolvedUser(res);
            Flash.success(`Hare Krishna ${resolvedUser?.name ?? ""}`.trim(), "Welcome back!");
            await handleAuthenticatedUser(res, normalizedIdentifier, uuid);
        } catch (err: any) {
            Flash.error(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DivineBackground overlayColor="#617EA4" overlayOpacity={0.9}>
            <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === "ios" ? "padding" : undefined}>
                <View style={styles.center}>
                    <View style={styles.logoWrap}>
                        <Image source={require("../../../assets/isckon.jpeg")} style={styles.logo} resizeMode="cover" />
                    </View>
                    <View style={styles.card}>
                        <View style={{ alignItems: "center", marginBottom: 18 }}>
                            <Text style={styles.title}>
                                हरे कृष्ण हरे कृष्ण कृष्ण कृष्ण हरे हरे |{"\n"}
                                हरे राम हरे राम राम राम हरे हरे ॥
                            </Text>
                            <Text style={styles.subtitle}>Sign in to your spiritual journey</Text>
                        </View>
                        <View style={{ gap: 14 }}>
                            {configLoading ? <View style={styles.configCard}><ActivityIndicator size="small" color="#F97316" /><Text style={styles.configText}>Loading sign-in options...</Text></View> : null}
                            {canChooseLoginMethod ? (
                                <View style={styles.methodWrap}>
                                    <Text style={styles.methodLabel}>Sign in using</Text>
                                    <View style={styles.methodRow}>
                                        <Pressable onPress={() => setLoginMethod("mobile")} style={[styles.methodChip, loginMethod === "mobile" ? styles.methodChipActive : null]}><Text style={[styles.methodChipText, loginMethod === "mobile" ? styles.methodChipTextActive : null]}>Mobile Number</Text></Pressable>
                                        <Pressable onPress={() => setLoginMethod("email")} style={[styles.methodChip, loginMethod === "email" ? styles.methodChipActive : null]}><Text style={[styles.methodChipText, loginMethod === "email" ? styles.methodChipTextActive : null]}>Email Address</Text></Pressable>
                                    </View>
                                </View>
                            ) : null}
                            <Input label={identifierLabel} placeholder={identifierPlaceholder} keyboardType={loginMethod === "email" ? "email-address" : "number-pad"} autoCapitalize="none" autoCorrect={false} maxLength={loginMethod === "mobile" ? 10 : undefined} value={identifier} onChangeText={(value) => setIdentifier(loginMethod === "mobile" ? value.replace(/\D/g, "").slice(0, 10) : value.replace(/\s/g, ""))} />
                            <View style={styles.mpinContainer}>
                                <Input label="MPIN" placeholder="Enter 6-digit MPIN" keyboardType="number-pad" secureTextEntry={!showMpin} maxLength={6} value={mpin} onChangeText={(value) => setMpin(value.replace(/\D/g, ""))} style={{ paddingRight: 40 }} />
                                <Pressable style={styles.eyeIcon} onPress={() => setShowMpin(!showMpin)}><Icon name={showMpin ? "eye-off-outline" : "eye-outline"} size={22} color="#6B7280" /></Pressable>
                            </View>
                            <View style={{ alignItems: "flex-end" }}><Pressable disabled={loading} onPress={() => navigation.navigate("ForgotMPIN")}><Text style={styles.link}>Forgot MPIN?</Text></Pressable></View>
                            <Button title={loading ? "Verifying..." : "Login"} onPress={handleLogin} disabled={loading || configLoading || (!mobileEnabled && !emailEnabled)} />
                        </View>
                        <View style={{ marginTop: 18, alignItems: "center" }}><Text style={styles.footerText}>Don&apos;t have an account? <Text style={styles.register} onPress={() => navigation.navigate("Register")}>Register</Text></Text></View>
                    </View>
                </View>
            </KeyboardAvoidingView>
            <Modal visible={showReverifyModal} transparent animationType="fade" onRequestClose={closeReverifyFlow}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <Text style={styles.modalTitle}>Reverify Account</Text>
                        <Text style={styles.modalSubtitle}>Enter your mobile number or email address to receive a new OTP.</Text>
                        {reverifyError ? <Text style={styles.modalErrorText}>{reverifyError}</Text> : null}
                        <View style={styles.modalFields}>
                            <Input
                                label="Mobile Number"
                                placeholder="Enter 10 digit mobile number"
                                keyboardType="number-pad"
                                maxLength={10}
                                value={reverifyMobileNumber}
                                editable={!isReverifyMobileDisabled}
                                onChangeText={(value) => {
                                    if (isReverifyMobileDisabled) return;
                                    setReverifyMobileNumber(value.replace(/\D/g, "").slice(0, 10));
                                    if (reverifyError) setReverifyError("");
                                }}
                                style={isReverifyMobileDisabled ? { opacity: 0.6 } : undefined}
                            />

                            <Input
                                label="Email Address"
                                placeholder="Enter registered email address"
                                keyboardType="email-address"
                                autoCapitalize="none"
                                autoCorrect={false}
                                value={reverifyEmail}
                                onChangeText={(value) => {
                                    setReverifyEmail(value.replace(/\s/g, ""));
                                    if (reverifyError) setReverifyError("");
                                }}
                            />
                        </View>
                        <View style={styles.modalButtonRow}>
                            <Pressable onPress={closeReverifyFlow} disabled={loading} style={[styles.modalSecondaryButton, loading ? styles.modalButtonDisabled : null]}><Text style={styles.modalSecondaryButtonText}>Cancel</Text></Pressable>
                            <View style={styles.modalPrimaryButtonWrap}><Button title={loading ? "Processing..." : "Reverify"} onPress={submitReverifyRequest} disabled={loading} /></View>
                        </View>
                    </View>
                </View>
            </Modal>
            <Modal visible={showOtpModal} transparent animationType="fade" onRequestClose={resetOtpModalState}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <Text style={styles.modalTitle}>Verify OTP</Text>
                        <Text style={styles.modalSubtitle}>{otpVerificationHint}</Text>
                        {otpErrorMessage ? <Text style={styles.modalErrorText}>{otpErrorMessage}</Text> : null}
                        <View style={styles.modalFields}>
                            {otpDeliveryInfo.mobile ? <Input label={mobileOtpLabel} placeholder={OTP_PLACEHOLDER} keyboardType="number-pad" maxLength={OTP_MAX_LENGTH} value={mobileOtp} onChangeText={(value) => { setMobileOtp(value.replace(/\D/g, "")); if (otpErrorMessage) setOtpErrorMessage(""); }} textAlign="center" style={styles.otpInput} /> : null}
                            {otpDeliveryInfo.email ? <Input label={emailOtpLabel} placeholder={OTP_PLACEHOLDER} keyboardType="number-pad" maxLength={OTP_MAX_LENGTH} value={emailOtp} onChangeText={(value) => { setEmailOtp(value.replace(/\D/g, "")); if (otpErrorMessage) setOtpErrorMessage(""); }} textAlign="center" style={styles.otpInput} /> : null}
                        </View>
                        {resendTimer > 0 ? <Text style={styles.timerText}>Resend OTP in {resendTimeLabel}</Text> : <Pressable onPress={handleResendOtp} disabled={loading} style={styles.resendWrap}><Text style={[styles.resendText, loading ? styles.modalButtonDisabled : null]}>{loading ? "Sending..." : "Resend OTP"}</Text></Pressable>}
                        <View style={styles.modalButtonRow}>
                            <Pressable onPress={resetOtpModalState} disabled={loading} style={[styles.modalSecondaryButton, loading ? styles.modalButtonDisabled : null]}><Text style={styles.modalSecondaryButtonText}>Cancel</Text></Pressable>
                            <View style={styles.modalPrimaryButtonWrap}><Button title={loading ? "Verifying..." : "Verify OTP"} onPress={handleVerifyOtp} disabled={loading || !canVerifyOtp} /></View>
                        </View>
                    </View>
                </View>
            </Modal>

            <Modal
                visible={authErrorDialogVisible}
                transparent
                animationType="fade"
                onRequestClose={closeAuthErrorDialog}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <Text style={styles.modalTitle}>Login Failed</Text>

                        <Text style={styles.modalSubtitle}>
                            {authErrorMessage}
                        </Text>

                        <View style={styles.supportWrap}>
                            <Text style={styles.supportText}>Contact on </Text>
                            <Pressable onPress={handleSupportEmailPress}>
                                <Text style={styles.supportEmailLink}>
                                    info@gaurpremaashray.co.in
                                </Text>
                            </Pressable>
                        </View>

                        <View style={styles.singleButtonRow}>
                            <Pressable style={styles.okButton} onPress={closeAuthErrorDialog}>
                                <Text style={styles.okButtonText}>OK</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            </Modal>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    center: { flex: 1, paddingHorizontal: 16, justifyContent: "center", alignItems: "center" },
    logoWrap: { width: 76, height: 76, borderRadius: 22, backgroundColor: "rgba(255,255,255,0.30)", borderWidth: 1, borderColor: "rgba(255,255,255,0.35)", alignItems: "center", justifyContent: "center", marginBottom: 14, overflow: "hidden" },
    logo: { width: 70, height: 70, borderRadius: 18 },
    card: { width: "100%", maxWidth: 380, backgroundColor: "rgba(255,255,255,0.90)", borderRadius: 22, padding: 18, borderWidth: 1, borderColor: "rgba(251,146,60,0.20)", shadowColor: "#000", shadowOpacity: 0.12, shadowRadius: 18, shadowOffset: { width: 0, height: 10 }, elevation: 6 },
    title: { fontSize: 18, fontWeight: "800", color: "#9A3412", textAlign: "center", },
    subtitle: { marginTop: 4, fontSize: 12, fontWeight: "600", color: "rgba(124,45,18,0.55)" },
    link: { fontSize: 12, fontWeight: "700", color: "rgba(234,88,12,0.95)" },
    footerText: { fontSize: 12, fontWeight: "600", color: "rgba(74,35,17,0.65)" },
    register: { color: "rgba(234,88,12,0.95)", fontWeight: "900" },
    configCard: { flexDirection: "row", alignItems: "center", gap: 10, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10, backgroundColor: "rgba(255,237,213,0.55)", borderWidth: 1, borderColor: "rgba(251,146,60,0.18)" },
    configText: { fontSize: 12, fontWeight: "700", color: "rgba(124,45,18,0.78)" },
    methodWrap: { gap: 8 },
    methodLabel: { fontSize: 12, fontWeight: "700", color: "rgba(74,35,17,0.70)" },
    methodRow: { flexDirection: "row", gap: 10 },
    methodChip: { flex: 1, minHeight: 42, borderRadius: 12, alignItems: "center", justifyContent: "center", borderWidth: 1, borderColor: "rgba(251,146,60,0.24)", backgroundColor: "rgba(255,255,255,0.8)", paddingHorizontal: 10 },
    methodChipActive: { backgroundColor: "rgba(249,115,22,0.12)", borderColor: "rgba(249,115,22,0.55)" },
    methodChipText: { fontSize: 12, fontWeight: "800", color: "rgba(74,35,17,0.75)" },
    methodChipTextActive: { color: "#9A3412" },
    mpinContainer: { position: "relative" },
    eyeIcon: { position: "absolute", right: 12, bottom: 12 },
    modalOverlay: { flex: 1, backgroundColor: "rgba(15,23,42,0.45)", alignItems: "center", justifyContent: "center", paddingHorizontal: 20 },
    modalCard: { width: "100%", maxWidth: 380, borderRadius: 22, backgroundColor: "rgba(255,255,255,0.96)", padding: 18, borderWidth: 1, borderColor: "rgba(251,146,60,0.20)", shadowColor: "#000", shadowOpacity: 0.16, shadowRadius: 20, shadowOffset: { width: 0, height: 10 }, elevation: 8, gap: 14 },
    modalTitle: { fontSize: 18, fontWeight: "800", color: "#4A2311", textAlign: "center" },
    modalSubtitle: { fontSize: 12.5, fontWeight: "600", color: "rgba(74,35,17,0.65)", textAlign: "center", lineHeight: 18 },
    modalFields: { gap: 12 },
    modalErrorText: { fontSize: 12, fontWeight: "700", color: "rgba(220,38,38,0.95)", textAlign: "center" },
    modalButtonRow: { flexDirection: "row", alignItems: "center", gap: 10 },
    modalSecondaryButton: { minWidth: 92, height: 46, borderRadius: 10, borderWidth: 1, borderColor: "rgba(148,163,184,0.45)", alignItems: "center", justifyContent: "center", paddingHorizontal: 14, backgroundColor: "rgba(255,255,255,0.92)" },
    modalSecondaryButtonText: { fontSize: 13, fontWeight: "800", color: "rgba(71,85,105,0.95)" },
    modalPrimaryButtonWrap: { flex: 1 },
    modalButtonDisabled: { opacity: 0.6 },
    otpInput: { fontSize: 18, fontWeight: "700", letterSpacing: 6 },
    timerText: { fontSize: 12, fontWeight: "700", color: "rgba(74,35,17,0.65)", textAlign: "center" },
    resendWrap: { alignItems: "center" },
    resendText: { fontSize: 12, fontWeight: "800", color: "rgba(234,88,12,0.95)" },
    supportWrap: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
    },

    supportText: {
        fontSize: 13,
        fontWeight: "600",
        color: "rgba(74,35,17,0.75)",
    },

    supportEmailLink: {
        fontSize: 13,
        fontWeight: "800",
        color: "#2563EB",
        textDecorationLine: "underline",
    },

    singleButtonRow: {
        marginTop: 6,
    },

    okButton: {
        height: 46,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F97316",
    },

    okButtonText: {
        color: "#FFFFFF",
        fontSize: 14,
        fontWeight: "800",
    },
});
